/**
 * Rounds a number to a fixed number of decimal places, stripping the
 * IEEE-754 floating point artifacts that appear after arithmetic such as
 * multiplying or dividing by 100.
 */
const roundToDecimalPlaces = (value: number, decimalPlaces: number): number => {
    const factor = Math.pow(10, decimalPlaces);
    return Math.round(value * factor) / factor;
};

// Percentages are edited/displayed with at most a couple of decimal places, so
// six decimal places on the percentage (and eight on the underlying fraction)
// is well beyond any real precision while still absorbing floating point noise.
const PERCENTAGE_DECIMAL_PLACES = 6;
const FRACTION_DECIMAL_PLACES = PERCENTAGE_DECIMAL_PLACES + 2;

/**
 * Converts a decimal fraction (how percentages are persisted, e.g. `0.29`) to
 * the whole-percentage value shown in the UI (e.g. `29`).
 *
 * A plain `fraction * 100` is not safe here: `0.29 * 100` evaluates to
 * `28.999999999999996`, which renders as a garbled value in a number input.
 * Rounding removes that noise while preserving legitimate fractional
 * percentages (e.g. `0.155` -> `15.5`).
 */
export const fractionToPercentage = (fraction: number): number =>
    roundToDecimalPlaces(fraction * 100, PERCENTAGE_DECIMAL_PLACES);

/**
 * Converts a whole-percentage value from the UI (e.g. `29`) back to the decimal
 * fraction that is persisted (e.g. `0.29`), rounding away floating point noise
 * introduced by the division.
 */
export const percentageToFraction = (percentage: number): number =>
    roundToDecimalPlaces(percentage / 100, FRACTION_DECIMAL_PLACES);
